const GOOGLE_TRANSLATE_CLASSES = ['translated-ltr', 'translated-rtl'];

type PatchedNodePrototype = Node & {
  __jaradGoogleTranslateCompatibilityCleanup__?: () => void;
};

const hasGoogleTranslateWrapper = (node: Node | null): boolean => {
  let current = node;

  while (current) {
    if (current.nodeName === 'FONT') {
      return true;
    }

    current = current.parentNode;
  }

  return false;
};

const isGoogleTranslatedDocument = (node: Node | null): boolean => {
  const documentElement = node?.ownerDocument?.documentElement ?? document.documentElement;

  return GOOGLE_TRANSLATE_CLASSES.some((className) =>
    documentElement.classList.contains(className)
  ) || hasGoogleTranslateWrapper(node);
};

const findDirectChild = (parent: Node, descendant: Node): Node | null => {
  let current: Node | null = descendant;

  while (current?.parentNode && current.parentNode !== parent) {
    current = current.parentNode;
  }

  return current?.parentNode === parent ? current : null;
};

/**
 * Google Translate wraps React text nodes in FONT elements. React keeps the
 * original node references, so later updates can try to remove or insert next
 * to a node that is no longer a direct child of the expected parent.
 *
 * This patch leaves normal DOM operations untouched. It only repairs a
 * mismatched operation while Google Translate is active or one of its FONT
 * wrappers is present.
 *
 * @see https://github.com/react/react/issues/11538
 */
export const installGoogleTranslateDomCompatibility = (): (() => void) => {
  if (typeof Node === 'undefined' || typeof document === 'undefined') {
    return () => undefined;
  }

  const prototype = Node.prototype as PatchedNodePrototype;
  if (prototype.__jaradGoogleTranslateCompatibilityCleanup__) {
    return prototype.__jaradGoogleTranslateCompatibilityCleanup__;
  }

  const originalRemoveChild = prototype.removeChild;
  const originalInsertBefore = prototype.insertBefore;

  const patchedRemoveChild = function removeChild<T extends Node>(this: Node, child: T): T {
    if (child.parentNode === this || !isGoogleTranslatedDocument(child)) {
      return originalRemoveChild.call(this, child) as T;
    }

    const translatedWrapper = findDirectChild(this, child);
    if (translatedWrapper) {
      originalRemoveChild.call(this, translatedWrapper);
    }

    return child;
  };

  const patchedInsertBefore = function insertBefore<T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null
  ): T {
    if (
      referenceNode === null ||
      referenceNode.parentNode === this ||
      !isGoogleTranslatedDocument(referenceNode)
    ) {
      return originalInsertBefore.call(this, newNode, referenceNode) as T;
    }

    const translatedWrapper = findDirectChild(this, referenceNode);
    return originalInsertBefore.call(this, newNode, translatedWrapper) as T;
  };

  prototype.removeChild = patchedRemoveChild;
  prototype.insertBefore = patchedInsertBefore;

  const cleanup = () => {
    if (prototype.removeChild === patchedRemoveChild) {
      prototype.removeChild = originalRemoveChild;
    }

    if (prototype.insertBefore === patchedInsertBefore) {
      prototype.insertBefore = originalInsertBefore;
    }

    delete prototype.__jaradGoogleTranslateCompatibilityCleanup__;
  };

  Object.defineProperty(prototype, '__jaradGoogleTranslateCompatibilityCleanup__', {
    configurable: true,
    value: cleanup,
  });

  return cleanup;
};
